import { useTranslation } from "react-i18next";
import { useGetABOProfileQuery } from "../../../../services/AboProfile/AboProfileAPI";
import { useAppSelector, RootState } from "../../../../store";
import KeyInformationBlock from "../../KeyInformation/KeyInformation";
import defaultContent from "../../../../content/downlineProfile";
import "../../KeyInformation/KeyInformation.scss";
import KeyInformationAwardsRow from "../../KeyInformation/Awards/Awards";
import KeyInformationKeyDatesRow from "../../KeyInformation/KeyDates/KeyDates";
import KeyInformationAdditionalInformationRow from "../../KeyInformation/GenericIndicators/GenericIndicators";
import KeyInformationSponsorRow from "../../KeyInformation/Sponsor/Sponsor";
import KeyInformationApplicantWrapper from "../../KeyInformation/Applicant/Applicant";
import {
  transformAdditionalRegInfo,
  transformAwardInfo,
  transformKeyDates,
  transformSponsor,
} from "../../KeyInformation/Transforms/transformKeyInformation";
import { ErrorTile } from "../../shared/ErrorTile/ErrorTile";
import KeyInformationCPISWrapper from "../../KeyInformation/CorePlusIncentiveStatus/CorePlusIncentiveStatus";
import useGetProfileImage from "../../hooks/useGetProfileImage";

interface KeyInformationProps {
  showCpis?: boolean;
}
const KeyInformation = ({ showCpis }: KeyInformationProps) => {
  const { t } = useTranslation(["downlineProfile"]);
  const affAbo = useAppSelector((state: RootState) => state.downlineProfile.affAbo);
  const { showAutoRenewal, showContractSigned, showOrderBlock, showSponsorBlock } =
    useAppSelector((state: RootState) => state.boot.configuration.profile)?.additionalRegInfo ?? {};
  const showRenewalDate = useAppSelector(
    (state: RootState) => state.boot.configuration.profile?.aboInfo?.showRenewalDate ?? true,
  );
  const showBirthday = useAppSelector(
    (state: RootState) => state.boot.configuration.profile?.aboInfo?.showBirthday ?? true,
  );

  const [aff = "", abo = ""] = affAbo.split("-");
  const isFromEdLos = useAppSelector((state: RootState) => state.profile.isFromEdLos);
  const { data, isFetching, isError, refetch } = useGetABOProfileQuery(
    { aff, abo, ...(isFromEdLos ? { includePlatinumAndAbove: true } : {}) },
    { skip: !aff || !abo },
  );
  const {
    aboInfo,
    pinAwardSummary,
    partyInfo,
    additionalRegInfo,
    orderInfo,
    isPlatinumOrAbove = false,
    isEmeraldAndAbove = false,
    isDiamondAndAbove = false,
  } = data ?? {};

  const transformedAwardInfo = transformAwardInfo(pinAwardSummary);
  const transformedKeyDates = transformKeyDates(aboInfo, orderInfo, showRenewalDate, showBirthday);
  const transformedAdditionalInfo = transformAdditionalRegInfo(additionalRegInfo);
  const hasAdditionalInfo = showOrderBlock || showSponsorBlock || showAutoRenewal || showContractSigned;
  const isAwardMasked = isPlatinumOrAbove || isEmeraldAndAbove || isDiamondAndAbove;
  const transformedSponsors = transformSponsor(aboInfo);

  const sponsorProfileImageEntries = transformedSponsors.map((s) => {
    return {
      affAbo: `${s.affNo}-${s.aboNo}`,
      imageRefUrl: s.imageRefUrl ?? "",
    };
  });
  const {
    profileImages,
    isLoading: isProfileImagesLoading,
    partyImages,
  } = useGetProfileImage([...sponsorProfileImageEntries, { affAbo, imageRefUrl: aboInfo?.imageRefUrl ?? "" }], false, {
    affAbo: affAbo,
    partyData:
      partyInfo?.map((applicant) => {
        return { partyId: applicant.partyId, imageRefUrl: applicant.imageRefUrl ?? "" };
      }) ?? [],
  });

  if (isError)
    return (
      <>
        <div className="error-tile-wrapper">
          <ErrorTile
            clickFunction={() => refetch()}
            isLoading={isFetching}
            errorStatus={isError}
            tealiumTrackId="downline-profile-key-information"
          />
        </div>
      </>
    );
  return (
    <>
      <div className="keyInformation">
        <div>
          <KeyInformationBlock
            header={t("keyInformation.awardInformation", defaultContent.keyInformation.awardInformation)}
            isLoading={isFetching}
          >
            {!isAwardMasked ? (
              transformedAwardInfo.map((award, i) => <KeyInformationAwardsRow key={i} {...award} />)
            ) : (
              <p className="award-info-empty">
                {t("keyInformation.awardInformationEmpty", defaultContent.keyInformation.awardInformationEmpty)}
              </p>
            )}
          </KeyInformationBlock>
          <KeyInformationBlock
            header={t("keyInformation.keyDates", defaultContent.keyInformation.keyDates)}
            isLoading={isFetching}
          >
            {transformedKeyDates.map((date, i) => {
              return <KeyInformationKeyDatesRow key={i} {...date} />;
            })}
          </KeyInformationBlock>
          {hasAdditionalInfo && (
            <KeyInformationBlock
              header={t("keyInformation.additionalInformation", defaultContent.keyInformation.additionalInformation)}
              isLoading={isFetching}
            >
              {transformedAdditionalInfo.map((info, i) => (
                <KeyInformationAdditionalInformationRow
                  key={i}
                  {...info}
                  configs={{ showOrderBlock, showSponsorBlock, showAutoRenewal, showContractSigned }}
                />
              ))}
            </KeyInformationBlock>
          )}
          {showCpis && <KeyInformationCPISWrapper />}
        </div>
        <div>
          <KeyInformationBlock
            header={t("keyInformation.applicants", defaultContent.keyInformation.applicants)}
            isLoading={isFetching || isProfileImagesLoading}
          >
            <KeyInformationApplicantWrapper
              partyInfo={partyInfo}
              privacyFlag={aboInfo?.privacyFlag}
              isKoreaMarket={aboInfo?.countryCode?.toUpperCase() === "KR"}
              partyImages={partyImages}
              primaryApplicantImage={
                profileImages.find(
                  (e) =>
                    String(e.abo) === String(abo) && String(e.aff).padStart(3, "0") === String(aff).padStart(3, "0"),
                )?.profileImage ?? ""
              }
            />
          </KeyInformationBlock>
          <KeyInformationBlock
            header={t("keyInformation.sponsors", defaultContent.keyInformation.sponsors)}
            isLoading={isFetching || isProfileImagesLoading}
          >
            {...transformedSponsors.map((spon, i) => {
              const imageRefUrl =
                profileImages.find(
                  (e) =>
                    String(e.abo) === String(spon.aboNo) &&
                    String(e.aff).padStart(3, "0") === String(spon.affNo).padStart(3, "0"),
                )?.profileImage ?? "";
              return (
                <KeyInformationSponsorRow
                  key={i}
                  {...spon}
                  imageRefUrl={imageRefUrl}
                  isKoreaMarket={aboInfo?.countryCode?.toUpperCase() === "KR"}
                />
              );
            })}
          </KeyInformationBlock>
        </div>
      </div>
    </>
  );
};

export default KeyInformation;
